import React, { useState, useEffect } from 'react';
import { Loader2, Coins, Calendar, Info } from 'lucide-react';
import { pointService } from '../../services/pointService';
import { PointLog, Language } from '../../types';
import { t } from '../../i18n';

interface PointHistoryViewProps {
  userId: string;
  lang: string;
}

export const PointHistoryView: React.FC<PointHistoryViewProps> = ({ userId, lang }) => {
  const currentLang = lang as Language;
  const [logs, setLogs] = useState<PointLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLogs = async () => {
      setIsLoading(true);
      try {
        const data = await pointService.getPointLogs(userId);
        setLogs(data);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    loadLogs();
  }, [userId]);

  const formatDate = (dateStr: string) => {
    // DB의 created_at이 타임존 정보가 없는 문자열(2026-01-28 16:16:27.057718)일 경우
    // 브라우저가 이를 로컬 시간으로 오해할 수 있으므로, 명시적으로 'Z'를 붙여 UTC임을 알립니다.
    const normalizedDateStr = dateStr.includes('Z') || dateStr.includes('+') 
      ? dateStr 
      : `${dateStr.replace(' ', 'T')}Z`;
      
    const d = new Date(normalizedDateStr);
    const timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Seoul'
        ? 'KST'
        : 'UTC';

    if (lang === 'ko') {
      return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} (${timezone})`;
    }
    return `${d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })} (${timezone})`;
  };

  const getTranslatedReason = (reason: string) => {
    switch (reason) {
      case 'Daily Attendance':
        return t(currentLang, 'reasonDailyAttendance');
      case '7-Day Attendance Bonus':
        return t(currentLang, 'reason7DayBonus');
      case 'News Feed Post':
        return t(currentLang, 'reasonNewsPost');
      case 'News Feed Delete':
      case '피드 삭제':
        return t(currentLang, 'reasonNewsDelete');
      case 'News Comment Post':
        return t(currentLang, 'reasonCommentPost');
      case 'News Comment Delete':
        return t(currentLang, 'reasonCommentDelete');
      case 'Admin Special Reward':
      case '운영자 특별 보상':
        return t(currentLang, 'reasonAdminSpecialReward');
      case 'Play Game: NEON PITCH':
        return t(currentLang, 'reasonNeonPitch');
      case 'Footy Master Entry Fee':
        return t(currentLang, 'reasonFootyMaster');
      case 'Triple Bet Multiple Selection':
        return t(currentLang, 'reasonTripleBetMultiple');
      case 'Triple Bet AI Analysis':
        return t(currentLang, 'reasonTripleBetAi');
      default:
        if (reason.startsWith('AI Report:')) {
          return t(currentLang, 'reasonAiReport');
        }
        return reason || t(currentLang, 'reasonRegularEarn');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="animate-spin text-rose-500 mb-4" size={32} />
        <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">
          {t(currentLang, 'syncingPointHistory')}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 pb-40 sm:p-8">
      <div className="bg-slate-50 rounded-3xl p-6 mb-8 border border-slate-100 flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center shrink-0">
          <Info size={20} />
        </div>
        <div>
          <h4 className="text-slate-900 font-black uppercase text-sm mb-1 tracking-tight">
            {t(currentLang, 'pointGuide')}
          </h4>
          <p className="text-slate-500 text-xs font-medium leading-relaxed">
            {t(currentLang, 'pointGuideDesc')}
          </p>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="py-20 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-200 mb-4 mx-auto">
            <Coins size={32} />
          </div>
          <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">
            {t(currentLang, 'noPointHistory')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <div key={log.id} className="bg-white border border-gray-100 rounded-2xl p-5 flex items-center justify-between md:hover:border-rose-100 transition-colors group">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-black text-sm ${log.amount >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}>
                  {log.amount >= 0 ? `+${log.amount}` : log.amount}
                </div>
                <div className="flex flex-col">
                  <span className="text-slate-900 font-bold text-sm mb-1">{getTranslatedReason(log.reason)}</span>
                  <div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                    <Calendar size={12} />
                    <span>{formatDate(log.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

import { ChevronRight } from 'lucide-react';
